/**
 * White Paper request modal.
 *
 * Triggered from the footer "White Paper" link on every public page
 * (homepage, About Us, R&D Symposium). Mirrors the dedicated whitepaper
 * form on the R&D Symposium page — same fields, same backend endpoint
 * (POST /api/v1/homepage/whitepaper) — but presented as a focused modal so
 * users on other pages can request the PDF without navigating.
 *
 * Sets `form_source = "footer_whitepaper_modal"` (overridable via prop) so
 * ops can distinguish modal-driven leads from the R&D Symposium-page form
 * in the `whitepaper_requests` table.
 */

import React, { useState, useEffect } from 'react';
import { CloseIcon } from '../../components/icons';
import { apiService } from '../../services/apiService';
import { friendlyAuthError } from '../../utils/authErrors';
import { InlineAlert } from '../../components/InlineAlert';
import { trackClick } from '../../utils/analytics';

interface WhitePaperModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Override the analytics form_source (defaults to footer_whitepaper_modal). */
  formSource?: string;
}

export const WhitePaperModal: React.FC<WhitePaperModalProps> = ({
  isOpen,
  onClose,
  formSource = 'footer_whitepaper_modal',
}) => {
  const [form, setForm] = useState({ name: '', email: '', org: '' });
  const [state, setState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // Reset state every time the modal is opened so a previous submission
  // doesn't leak into the next session of the form.
  useEffect(() => {
    if (isOpen) {
      setForm({ name: '', email: '', org: '' });
      setState('idle');
      setErrorMsg('');
      trackClick('whitepaper_modal_open', { source: formSource });
    }
  }, [isOpen, formSource]);

  if (!isOpen) return null;

  // Drop-off telemetry: which state the modal was in when the user dismissed
  // it. Helps split "tried submit, got an error, then bailed" from "opened
  // the modal but never engaged" — different problems, different fixes.
  const handleClose = () => {
    trackClick('whitepaper_modal_close', {
      source: formSource,
      state,
      had_input: !!(form.name.trim() || form.email.trim() || form.org.trim()),
    });
    onClose();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackClick('whitepaper_modal_submit', { email: form.email, source: formSource });

    // Distinguish client-side validation failures from API failures in
    // analytics so we can see drop-off causes separately.
    const missingName  = !form.name.trim();
    const missingEmail = !form.email.trim();
    const invalidEmail = !missingEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (missingName || missingEmail || invalidEmail) {
      setState('error');
      setErrorMsg('Please enter your name and a valid email.');
      trackClick('whitepaper_modal_validation_error', {
        source: formSource,
        missing_name: missingName,
        missing_email: missingEmail,
        invalid_email: invalidEmail,
      });
      return;
    }
    setErrorMsg('');
    setState('submitting');

    try {
      const res = await apiService.submitWhitepaperRequest({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        organisation: form.org.trim() || undefined,
        form_source: formSource,
      });
      if (res.success) {
        setState('success');
        trackClick('whitepaper_modal_submit_success', { email: form.email, source: formSource });
      } else {
        setState('error');
        setErrorMsg(friendlyAuthError(res.error, 'Could not send the whitepaper email. Please try again.'));
        trackClick('whitepaper_modal_submit_error', { error: res.error, source: formSource });
      }
    } catch (err: any) {
      setState('error');
      setErrorMsg(err?.message || 'Network error. Please check your connection and try again.');
      trackClick('whitepaper_modal_submit_error', { error: err?.message, source: formSource });
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[60] p-3 sm:p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whitepaper-modal-title"
    >
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-md relative my-auto">
        <button
          onClick={handleClose}
          aria-label="Close"
          className="absolute top-4 right-4 p-2.5 rounded-full bg-grayscale-1200 text-white hover:bg-grayscale-1100 shadow-md"
        >
          <CloseIcon />
        </button>

        <div className="p-5 sm:p-7">
          {state === 'success' ? (
            <div className="text-center py-4 sm:py-6">
              <div className="text-3xl mb-2">✓</div>
              <h3 id="whitepaper-modal-title" className="text-base sm:text-lg font-bold text-grayscale-1300 mb-2">
                Check your inbox.
              </h3>
              <p className="text-sm text-grayscale-1100">
                We&apos;ve emailed the whitepaper to{' '}
                <strong className="text-grayscale-1300 break-all">{form.email}</strong>. If you don&apos;t see it in a minute or two, check your spam folder.
              </p>
            </div>
          ) : (
            <>
              <h3 id="whitepaper-modal-title" className="text-base sm:text-lg font-bold text-grayscale-1300 mb-1">
                Get the White Paper
              </h3>
              <p className="text-xs sm:text-sm text-grayscale-1100 mb-4">
                A curated synthesis from the R&amp;D Symposium. We&apos;ll send it straight to your inbox — no spam, ever.
              </p>

              <form onSubmit={submit} className="flex flex-col gap-3">
                <div>
                  <label htmlFor="wp-modal-name" className="block text-xs text-grayscale-1100 mb-1">Name</label>
                  <input
                    id="wp-modal-name"
                    type="text"
                    value={form.name}
                    onChange={e => {
                      setForm(f => ({ ...f, name: e.target.value }));
                      if (state === 'error') setState('idle');
                    }}
                    placeholder="Your full name"
                    className="w-full px-3 py-2.5 border border-grayscale-300 rounded-lg text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="wp-modal-email" className="block text-xs text-grayscale-1100 mb-1">Email</label>
                  <input
                    id="wp-modal-email"
                    type="email"
                    value={form.email}
                    onChange={e => {
                      setForm(f => ({ ...f, email: e.target.value }));
                      if (state === 'error') setState('idle');
                    }}
                    placeholder="you@company.com"
                    className="w-full px-3 py-2.5 border border-grayscale-300 rounded-lg text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="wp-modal-org" className="block text-xs text-grayscale-1100 mb-1">
                    Organisation (Optional)
                  </label>
                  <input
                    id="wp-modal-org"
                    type="text"
                    value={form.org}
                    onChange={e => setForm(f => ({ ...f, org: e.target.value }))}
                    placeholder="Your organisation"
                    className="w-full px-3 py-2.5 border border-grayscale-300 rounded-lg text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>
                <p className="text-[11px] text-grayscale-1100 leading-snug mt-1">
                  By submitting you agree to receive the white paper and occasional updates from UniversityTech.io. Unsubscribe any time.
                </p>
                {state === 'error' && errorMsg && (
                  <InlineAlert variant="error" compact>{errorMsg}</InlineAlert>
                )}
                <div className="flex justify-end mt-1">
                  <button
                    type="submit"
                    disabled={state === 'submitting'}
                    className="px-5 py-2.5 rounded-full bg-primary-600 hover:bg-primary-700 disabled:bg-grayscale-300 text-white text-sm font-medium inline-flex items-center justify-center gap-1.5 whitespace-nowrap"
                  >
                    <span>{state === 'submitting' ? 'Sending…' : 'Send me the whitepaper'}</span>
                    <span aria-hidden="true">→</span>
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhitePaperModal;
